import { useState, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import * as api from '../../services/api';

export const ModalViewCampaign = memo(({ id, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getCampaignDetail(id);
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load email');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };

  return createPortal(
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          width: '100%', maxWidth: 640, maxHeight: '85vh',
          background: 'rgba(255,255,255,.97)', backdropFilter: 'blur(40px)',
          borderRadius: 22,
          boxShadow: '0 32px 80px rgba(0,0,0,.18),0 8px 32px rgba(0,0,0,.1)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid rgba(0,0,0,.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 13,
              background: 'rgba(8,145,178,.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20,
            }}>📧</div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 720, color: 'var(--t1)', letterSpacing: '-.3px' }}>View Email</div>
              <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>
                {detail ? fmtDate(detail.created_at) : 'Loading detail…'}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 10,
              border: '1px solid rgba(0,0,0,.1)', background: 'rgba(0,0,0,.04)',
              cursor: 'pointer', fontSize: 14, color: 'var(--t2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {loading && (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--t3)' }}>Loading email…</div>
          )}
          {error && (
            <div style={{
              padding: '12px 14px', background: 'rgba(220,38,38,.08)',
              border: '1px solid rgba(220,38,38,.2)', borderRadius: 10,
              fontSize: 12.5, color: 'var(--red)',
            }}>⚠️ {error}</div>
          )}
          {!loading && !error && detail && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--t4)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Subject</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--t1)', marginBottom: 14 }}>{detail.subject || '—'}</div>

              <div style={{ display: 'flex', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
                <div style={{
                  padding: '8px 14px', borderRadius: 10,
                  background: 'rgba(8,145,178,.08)', border: '1px solid rgba(8,145,178,.2)',
                }}>
                  <div style={{ fontSize: 10, color: 'var(--t3)', fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase' }}>Total Sent</div>
                  <div style={{ fontSize: 18, fontWeight: 720, color: 'var(--cyan)', marginTop: 2 }}>{detail.total}</div>
                </div>
                <div style={{
                  padding: '8px 14px', borderRadius: 10,
                  background: 'rgba(100,116,139,.08)', border: '1px solid rgba(100,116,139,.2)',
                }}>
                  <div style={{ fontSize: 10, color: 'var(--t3)', fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase' }}>Sent On</div>
                  <div style={{ fontSize: 13, fontWeight: 640, color: 'var(--t1)', marginTop: 4 }}>{fmtDate(detail.created_at)}</div>
                </div>
              </div>

              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--t4)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Message</div>
              <div
                className="email-content"
                style={{
                  border: '1px solid rgba(0,0,0,.08)', borderRadius: 12,
                  padding: 16, background: '#fafbfc',
                  maxHeight: 420, overflowY: 'auto',
                  fontSize: 13, lineHeight: 1.6, color: 'var(--t1)',
                }}
                dangerouslySetInnerHTML={{ __html: detail.message || '<em style="color:#94a3b8">No message body</em>' }}
              />
            </>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(0,0,0,.05)', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn bs" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
});
